import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import moment from "moment";

/**
 * Xuất Excel đơn giản cho Dialog xem chi tiết phiếu thu của 1 học sinh:
 * - 1 Bảng danh sách phiếu thu duy nhất khớp với giao diện hiển thị trong Dialog.
 * - Có dòng tổng cộng tiền ở cuối bảng.
 * 
 * @param {Object} params
 * @param {Array} params.invoices Danh sách các phiếu thu của học sinh
 * @param {String} params.studentName Tên học sinh
 * @param {String} params.studentCode Mã học sinh
 * @param {Object} params.summary Thông tin tóm tắt công nợ
 * @param {String} params.title Tiêu đề bảng tính
 */
export async function exportStudentInvoiceDetailExcel({
  invoices = [],
  studentName = "",
  studentCode = "",
  className = "",
  summary = {},
  title = "CHI TIẾT PHIẾU THU HỌC SINH",
}) {
  if (!Array.isArray(invoices) || invoices.length === 0) {
    throw new Error("Không có dữ liệu phiếu thu để xuất Excel");
  }

  const workbook = new ExcelJS.Workbook();
  workbook.creator = "OneKids";
  workbook.lastModifiedBy = "OneKids";
  workbook.created = new Date();
  workbook.modified = new Date();

  const worksheet = workbook.addWorksheet("ChiTietPhieuThu", {
    pageSetup: {
      paperSize: 9, // A4
      orientation: "landscape",
      fitToPage: true,
      fitToWidth: 1,
      fitToHeight: 0,
    },
  });

  const firstInv = invoices[0] || {};
  const effectiveName = studentName || firstInv.fullName || "—";
  const effectiveCode = studentCode || firstInv.studentCode || "—";
  const effectiveNickName = firstInv.nickName || "";
  const effectiveClass = className || firstInv.className || "—";
  const effectivePhone = firstInv.parentPhone || "—";

  // Danh sách cột hiển thị trong bảng đơn giản
  const headers = [
    "STT",
    "Mã phiếu thu",
    "Tháng",
    "Đợt",
    "Phạm vi",
    "Lớp / Nhóm / Gói",
    "Khoảng áp dụng",
    "Trạng thái",
    "Phải thu",
    "Thiếu/Thừa trước",
    "Đã thu",
    "Tiền mặt",
    "Chuyển khoản",
    "Thiếu/Thừa còn lại",
  ];

  const totalCols = headers.length;

  // --- 1. DÒNG THÔNG TIN ĐẦU TRANG ---
  // Dòng 1: Tiêu đề chính
  const row1 = worksheet.addRow([title]);
  row1.height = 32;
  row1.getCell(1).font = { name: "Times New Roman", bold: true, size: 16, color: { argb: "FF1E3A8A" } };
  row1.getCell(1).alignment = { horizontal: "center", vertical: "middle" };

  // Dòng 2: Thông tin học sinh
  const studentInfoStr = `Họ và tên: ${effectiveName}${effectiveNickName ? ` (${effectiveNickName})` : ""}   |   Mã HS: ${effectiveCode}   |   Lớp: ${effectiveClass}   |   SĐT phụ huynh: ${effectivePhone}`;
  const row2 = worksheet.addRow([studentInfoStr]);
  row2.height = 22;
  row2.getCell(1).font = { name: "Times New Roman", bold: true, size: 12, color: { argb: "FF334155" } };
  row2.getCell(1).alignment = { horizontal: "center", vertical: "middle" };

  // Dòng 3: Thống kê số tiền & Thời gian xuất
  const totalMoney = summary.totalMoney ?? invoices.reduce((s, i) => s + Number(i.sumMoney || 0), 0);
  const remainingMoney = summary.remainingMoney ?? invoices.reduce((s, i) => s + Number(i.afterMoney || 0), 0);
  const summaryStr = `Tổng số phiếu: ${invoices.length}   |   Tổng phải thu: ${Number(totalMoney).toLocaleString("vi-VN")} đ   |   Công nợ còn lại: ${Number(remainingMoney).toLocaleString("vi-VN")} đ   |   Ngày xuất: ${moment().format("DD/MM/YYYY HH:mm")}`;
  const row3 = worksheet.addRow([summaryStr]);
  row3.height = 20;
  row3.getCell(1).font = { name: "Times New Roman", italic: true, size: 11, color: { argb: "FF64748B" } };
  row3.getCell(1).alignment = { horizontal: "center", vertical: "middle" };

  // Dòng 4: Dòng trống
  worksheet.addRow([]);

  worksheet.mergeCells(1, 1, 1, totalCols);
  worksheet.mergeCells(2, 1, 2, totalCols);
  worksheet.mergeCells(3, 1, 3, totalCols);

  // --- 2. HEADER BẢNG DỮ LIỆU ---
  const headerRow = worksheet.addRow(headers);
  headerRow.height = 28;

  const borderThin = {
    top: { style: "thin", color: { argb: "FF94A3B8" } },
    left: { style: "thin", color: { argb: "FF94A3B8" } },
    bottom: { style: "thin", color: { argb: "FF94A3B8" } },
    right: { style: "thin", color: { argb: "FF94A3B8" } },
  };

  headerRow.eachCell((cell, colNumber) => {
    cell.font = { name: "Times New Roman", bold: true, size: 11, color: { argb: "FFFFFFFF" } };
    cell.alignment = { horizontal: "center", vertical: "middle", wrapText: true };
    cell.border = borderThin;

    // Phân màu: Thông tin chung (Xanh dương OneKids), Cột tiền (Xanh đậm)
    if (colNumber <= 8) {
      cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF2563EB" } }; // Primary Blue
    } else {
      cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF1D4ED8" } }; // Deep Blue
    }
  });

  // Thiết lập độ rộng cột
  worksheet.columns = [
    { width: 7 },  // STT
    { width: 19 }, // Mã phiếu thu
    { width: 11 }, // Tháng
    { width: 8 },  // Đợt
    { width: 13 }, // Phạm vi
    { width: 22 }, // Lớp / Nhóm / Gói
    { width: 25 }, // Khoảng áp dụng
    { width: 18 }, // Trạng thái
    { width: 16 }, // Phải thu
    { width: 16 }, // Thiếu/Thừa trước
    { width: 16 }, // Đã thu
    { width: 15 }, // Tiền mặt
    { width: 15 }, // Chuyển khoản
    { width: 16 }, // Thiếu/Thừa còn lại
  ];

  // Helper mappings
  const typeMap = { CLASS: "Lớp", GROUP: "Nhóm", PACKAGE: "Gói" };
  const statusMap = {
    UNBILLED: "Chưa có phiếu thu",
    UNPAID: "Chưa đóng 100%",
    PARTIALLY_PAID: "Chưa đóng đủ",
    FULLY_PAID: "Đóng đủ 100%",
    OVERPAID: "Đóng thừa",
    FULLY_PAID_WITH_SURPLUS: "Đóng đủ thừa",
    PAID_WITH_DEFICIT: "Đóng đủ thiếu",
    HIDE: "Ẩn phiếu thu",
  };

  const formatDate = (v) => {
    if (!v) return "—";
    const parts = String(v).split("-");
    if (parts.length === 3) return `${parts[2]}/${parts[1]}/${parts[0]}`;
    return v;
  };

  const formatMonth = (item) => {
    if (item.month && item.year) return `${item.month}/${item.year}`;
    if (item.startValidityPeriod) {
      const parts = String(item.startValidityPeriod).split("-");
      if (parts.length >= 2) return `${parseInt(parts[1], 10)}/${parts[0]}`;
    }
    return "—";
  };

  // Mảng tích lũy dòng tổng cộng
  const totals = new Array(totalCols).fill("");
  totals[0] = "";
  totals[1] = "Tổng cộng";

  const addToTotal = (colZeroIdx, val) => {
    if (val !== "" && val !== null && val !== undefined) {
      const num = Number(val);
      if (!isNaN(num)) {
        if (totals[colZeroIdx] === "") totals[colZeroIdx] = 0;
        totals[colZeroIdx] += num;
      }
    }
  };

  // --- 3. GHI DỮ LIỆU CÁC DÒNG PHIẾU THU ---
  invoices.forEach((item, index) => {
    const periodStr = item.startValidityPeriod && item.endValidityPeriod
      ? `${formatDate(item.startValidityPeriod)} — ${formatDate(item.endValidityPeriod)}`
      : "—";

    const rowData = [
      index + 1,
      item.invoiceCode || "—",
      formatMonth(item),
      item.turn || "—",
      typeMap[item.targetType] || item.targetType || "—",
      item.targetName || item.className || "—",
      periodStr,
      statusMap[item.status] || item.status || "—",
      Number(item.sumMoney || 0),
      Number(item.beforeMoney || 0),
      Number(item.collectedMoney || 0),
      Number(item.cashMoney || 0),
      Number(item.transferMoney || 0),
      Number(item.afterMoney || 0),
    ];

    // Cộng dồn 6 cột tiền tài chính (index 8 -> 13)
    for (let i = 8; i < 14; i++) {
      addToTotal(i, rowData[i]);
    }

    const dataRow = worksheet.addRow(rowData);
    dataRow.height = 22;

    dataRow.eachCell({ includeEmpty: true }, (cell, colNumber) => {
      cell.font = { name: "Times New Roman", size: 11 };
      cell.border = borderThin;

      const cIdx = colNumber - 1; // 0-based

      // Căn giữa cho STT, Mã, Tháng, Đợt, Phạm vi, Khoảng áp dụng, Trạng thái
      if (cIdx === 0 || cIdx === 1 || cIdx === 2 || cIdx === 3 || cIdx === 4 || cIdx === 6 || cIdx === 7) {
        cell.alignment = { horizontal: "center", vertical: "middle" };
      } else if (cIdx === 5) {
        // Căn trái cho tên Lớp / Nhóm / Gói
        cell.alignment = { horizontal: "left", vertical: "middle" };
      } else {
        // Căn phải và format số cho các cột tiền
        cell.alignment = { horizontal: "right", vertical: "middle" };
        cell.numFmt = "#,##0";
      }
    });
  });

  // --- 4. DÒNG TỔNG CỘNG ---
  if (invoices.length > 0) {
    const totalRow = worksheet.addRow(totals);
    totalRow.height = 25;

    totalRow.eachCell({ includeEmpty: true }, (cell, colNumber) => {
      cell.font = { name: "Times New Roman", bold: true, size: 11, color: { argb: "FF0F172A" } };
      cell.border = borderThin;
      cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFF1F5F9" } }; // Light Slate

      const cIdx = colNumber - 1;
      if (cIdx === 1) {
        cell.alignment = { horizontal: "center", vertical: "middle" };
      } else if (cIdx >= 8) {
        cell.alignment = { horizontal: "right", vertical: "middle" };
        if (typeof cell.value === "number") cell.numFmt = "#,##0";
      } else {
        cell.alignment = { horizontal: "center", vertical: "middle" };
      }
    });
  }

  // --- 5. LƯU VÀ TẢI FILE ---
  const buffer = await workbook.xlsx.writeBuffer();
  const timeStamp = moment().format("YYYYMMDD_HHmmss");
  const cleanCode = (effectiveCode || "HS").replace(/[^a-zA-Z0-9_-]/g, "");

  saveAs(
    new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    }),
    `Chi_Tiet_Phieu_Thu_${cleanCode}_${timeStamp}.xlsx`
  );
}
